// Phase 62 (Plan 62-06) — API client for the To Be Invoiced builder.
//
// Three endpoints:
//   GET /api/invoices/pending?period=YYYY-MM  — cross-project billing queue
//   GET /api/projects/:id/to-be-invoiced?period=YYYY-MM  — per-project builder data
//   PATCH /api/expense-receipts/:id  { billedAmount }  — inline billedAmount edit
//
// Types mirror the backend service exports from toBeInvoicedQueueService.ts
// exactly (Plan 04 response shapes). Decimal fields are serialised as numbers
// by Fastify's response schema; always call .toString() before new Decimal()
// if you need precise arithmetic.

import { apiGet, apiPatch } from './client';

// ===== Types =====

/**
 * MKS-207 — the review page's three item states.
 * 🟢 ready · 🟡 waiting (unapproved hours or an open timesheet) ·
 * 🔴 blocked (no rate — the hours would bill at zero).
 */
export type BillingItemState = 'ready' | 'waiting' | 'blocked';

export type ContractValidity = 'valid' | 'not_started' | 'expired' | 'unset';

export interface PersonRef {
  userId: number;
  userName: string;
}

export interface OpenTimesheetRow extends PersonRef {
  state: 'open' | 'submitted' | 'rejected';
}

export interface UnloggedAssigneeRow extends PersonRef {
  email: string;
  roleName: string | null;
}

/**
 * MKS-284 — why a queue row is red. One entry per thing somebody still has to
 * sort out before the project can be invoiced, with the people behind it.
 */
export type AttentionReasonCode = 'no_rate' | 'not_approved' | 'not_submitted' | 'nothing_logged';

export interface AttentionReason {
  code: AttentionReasonCode;
  users: PersonRef[];
}

/**
 * MKS-136 — a billing period waiting to be invoiced. `billedFrom`/`billedTo` is
 * the range an invoice for the period covers: for a phase period it opens after
 * the PREVIOUS period's actual end, so it can span several months.
 */
export interface ReadyBillingPeriod {
  id: number;
  projectId: number;
  name: string;
  sequence: number;
  type: 'recurring' | 'phase';
  plannedAmount: number | null;
  currency: string | null;
  billedFrom: string;
  billedTo: string;
}

export interface TBIProjectRow {
  projectId: number;
  projectName: string;
  paymentTypeId: number | null;
  packageAmount?: number;
  overageHours?: number;
  overageStatus: 'none' | 'needs_approval' | 'ready';
  externalCostsBilled: number;
  /**
   * MKS-284 — the queue row is only green (`ready`) or red
   * (`needs_attention`), and `reasons` says why it is red. `blocked` /
   * `needs_approval` are what an older backend still sends.
   */
  status: 'blocked' | 'needs_approval' | 'ready' | 'needs_attention';
  /** MKS-284 — absent from an older backend's row; empty on a green row. */
  reasons?: AttentionReason[];
  // MKS-207 — optional on the client so a response from an older backend
  // (or a test fixture written before this ticket) still type-checks.
  blocked?: boolean;
  blockedUsers?: PersonRef[];
  openTimesheets?: OpenTimesheetRow[];
  contractValidity?: ContractValidity;
  // MKS-205 — contract window ('YYYY-MM-DD'); null on either side = open-ended.
  contractStart?: string | null;
  contractEnd?: string | null;
  // MKS-205 — hours in the period still waiting for approval, which the
  // billable amount therefore leaves out. Absent while the flag is off.
  unapprovedHours?: number;
  /**
   * MKS-210 — the row's money in CZK. `packageCzk` is `packageAmount`
   * converted at the period-end ČNB fixing; `totalCzk` sums all four parts.
   * `fxUnavailable` means no fixing was reachable, so a non-CZK paušál is
   * MISSING from `totalCzk` rather than guessed at.
   */
  packageCzk?: number;
  overageBilling?: number;
  hourlyBilling?: number;
  totalCzk: number;
  fxUnavailable?: boolean;
  /**
   * MKS-136 — the project's billing periods that are ready to invoice. Absent
   * when the project has none.
   */
  billingPeriods?: ReadyBillingPeriod[];
  /**
   * MKS-280 — hours still uninvoiced in an earlier month that already has an
   * invoice. That month's own row is hidden (one invoice per period), so the
   * leftover rolls into this one until somebody bills it. `carriedOverBilling`
   * is already part of `totalCzk`. Absent while the flag is off, or when the
   * project has nothing left over.
   */
  carriedOverHours?: number;
  carriedOverBilling?: number;
  carriedOverPeriods?: string[];
}

export interface OverageEntryDetail {
  entryId: number;
  userId: number;
  userName: string;
  date: string;
  hours: number;
  overageSlice: number;
  roleId: number | null;
  roleName: string | null;
  hourlyRate: number;
  billing: number;
  hasRate: boolean;
}

export interface ExpenseReceiptRow {
  id: number;
  description: string | null;
  receiptDate: string | null;
  amount: number | null;
  billedAmount: number | null;
  /** MKS-207 — `blocked` when the receipt carries no billable figure at all. */
  state?: BillingItemState;
}

/** MKS-207 — the T&M / hourly drill-down, one row per person. */
/**
 * MKS-269 — the states an entry can be in on the review page. Approved is
 * ticked by default; the other three are shown, unticked, and can be ticked.
 */
export type BillableEntryState = 'approved' | 'submitted' | 'open' | 'rejected';

export interface HourlyUserRow extends PersonRef {
  hours: number;
  billing: number;
  entryIds: number[];
  blocked: boolean;
  openTimesheet: boolean;
  /**
   * MKS-269 — which state THIS row's hours are in. A person gets one row per
   * state they logged hours in, so approved and unapproved hours tick apart.
   * Optional on the wire: absent means a pre-MKS-269 backend (or the flag off),
   * where every row is approved by construction.
   */
  entryState?: BillableEntryState;
  /**
   * MKS-252 — this person's in-period hours that are not approved yet. They
   * are not part of `hours` / `billing`, which count approved entries only.
   * Optional on the wire so an older backend's response still renders.
   */
  unapprovedHours?: number;
  state: BillingItemState;
}

/** MKS-207 — the timesheet-sum vs. invoice-total figures. */
export interface BillingReconciliation {
  loggedHours: number;
  loggedBilling: number;
  approvedUninvoicedHours: number;
  approvedUninvoicedBilling: number;
  unapprovedHours: number;
  unapprovedBilling: number;
  alreadyInvoicedHours: number;
  alreadyInvoicedBilling: number;
}

export interface ProjectToBeInvoicedData {
  period: string;
  billable: boolean;
  paymentTypeId: number | null;
  package?: {
    balicekHours: number;
    paushalAmount: number;
    paushalCurrency: string;
    packageCzk: number;
    state?: BillingItemState;
  };
  overage?: {
    totalHours: number;
    totalBilling: number;
    /**
     * MKS-549 — the currency `totalBilling` and every entry's `hourlyRate` /
     * `billing` are in: the project's package currency, NOT the CZK ledger.
     * The backend bills overage at the project's own `ProjectOverageRate` and
     * converts nothing; the ČNB conversion happens when the invoice is
     * generated. Optional so a response from an older backend still
     * type-checks — the card falls back to its pre-ticket CZK rendering.
     */
    currency?: string;
    overageStatus: 'none' | 'needs_approval' | 'ready';
    entries: OverageEntryDetail[];
    state?: BillingItemState;
  };
  expenses: ExpenseReceiptRow[];
  hourlyEntries?: {
    totalHours: number;
    totalBilling: number;
    entryIds: number[];
    byUser?: HourlyUserRow[];
    state?: BillingItemState;
    // Phase 70 (currency-aware billing rates) — true when the period is
    // still open AND at least one entry's CZK figure was recomputed at
    // read time from a foreign-currency assignment rate (see 70-04's
    // getProjectToBeInvoicedData). false once the period is locked, or
    // for a CZK-only project where no conversion applies.
    provisional?: boolean;
  };
  /**
   * MKS-280 — hours still uninvoiced in an earlier month that already has an
   * invoice: what somebody left unticked, and what a backdated request added
   * after the fact. They roll into this period so they can finally be billed.
   *
   * Kept apart from `hourlyEntries` on purpose — the reconciliation table is
   * about THIS month's timesheets. Absent while the flag is off.
   */
  carriedOver?: {
    /** The closed months the hours are dated in, oldest first. */
    periods: string[];
    totalHours: number;
    totalBilling: number;
    entryIds: number[];
    byUser?: HourlyUserRow[];
    state?: BillingItemState;
  };
  previousInvoices: {
    id: number;
    from: string | null;
    to: string | null;
    invoicedAmount: number;
    invoiceNumber: string | null;
  }[];
  // ---- MKS-207 review-page additions ----
  contractValidity?: ContractValidity;
  openTimesheets?: OpenTimesheetRow[];
  unloggedAssignees?: UnloggedAssigneeRow[];
  blockedUsers?: PersonRef[];
  reconciliation?: BillingReconciliation;
  // MKS-205 — `previousInvoices` is capped at the 5 newest; this is how many
  // exist in total, so the card knows whether to offer "Show more".
  previousInvoicesTotal?: number;
  // MKS-205 — server-side duplicate-period guard (the capped list above can no
  // longer answer it on its own).
  hasInvoiceForPeriod?: boolean;
  // MKS-205 — hours in the period still waiting for approval; 0 when there are none.
  unapprovedHours?: number;
  // MKS-136 — which billing period this invoice is for. Absent when the
  // project has none.
  billingPeriods?: ReadyBillingPeriod[];
}

/** MKS-205 — "recent" is the capped default; "all" is what "Show more" asks for. */
export type InvoiceHistoryPage = 'recent' | 'all';

// ===== API functions =====

export function fetchPendingQueue(period: string, options: { contractValid?: boolean } = {}) {
  const params = new URLSearchParams({ period });
  // MKS-205 — only sent when the operator ticks "valid for this period".
  if (options.contractValid) params.set('contractValid', 'true');
  return apiGet<{ data: TBIProjectRow[] }>(`/invoices/pending?${params.toString()}`);
}

export function fetchProjectToBeInvoiced(
  projectId: number,
  period: string,
  options: { invoiceHistory?: InvoiceHistoryPage } = {},
) {
  const params = new URLSearchParams({ period });
  if (options.invoiceHistory) params.set('invoiceHistory', options.invoiceHistory);
  return apiGet<{ data: ProjectToBeInvoicedData }>(
    `/projects/${projectId}/to-be-invoiced?${params.toString()}`,
  );
}

/**
 * MKS-207 — people assigned to the project during the period who logged
 * nothing at all. A standalone endpoint because ticket D (Slack reminders)
 * reuses it; the review page also receives the same list inline on
 * `ProjectToBeInvoicedData`.
 */
export function fetchUnloggedAssignees(projectId: number, period: string) {
  return apiGet<{ data: UnloggedAssigneeRow[] }>(
    `/projects/${projectId}/unlogged-assignees?period=${encodeURIComponent(period)}`,
  );
}

export function patchBilledAmount(receiptId: number, billedAmount: number) {
  return apiPatch<{ data: ExpenseReceiptRow }>(`/expense-receipts/${receiptId}`, { billedAmount });
}
